import styles from './programme-workout-header.module.scss'
import { ProgrammeWorkout } from '../../dao/core-dao'
import { useProgramme } from '../../hook/use-programmes'
import { BackArrow } from '../programmes/programme-back-arrow'
import React from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'

interface ProgrammeWorkoutHeaderProps {
  programmeId: string
  currentProgrammeWorkoutId: string
  allProgrammeWorkouts: ProgrammeWorkout[]
}

export const ProgrammeWorkoutHeader = ({
  programmeId,
  currentProgrammeWorkoutId,
  allProgrammeWorkouts,
}: ProgrammeWorkoutHeaderProps): React.ReactElement => {
  const { data: programme, isLoading } = useProgramme(programmeId)
  const navigate = useNavigate()

  // Sort workouts by week first, then by order within each week
  const sortedWorkouts = [...allProgrammeWorkouts].sort((a, b) => {
    if (a.onWeek !== b.onWeek) return a.onWeek - b.onWeek
    return a.order - b.order
  })

  // Find current workout index
  const currentIndex = sortedWorkouts.findIndex((w) => w.id === currentProgrammeWorkoutId)
  const currentWorkout = sortedWorkouts[currentIndex]

  // Determine if prev/next are available
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < sortedWorkouts.length - 1

  const handlePrevious = (): void => {
    if (hasPrevious) {
      const prevWorkout = sortedWorkouts[currentIndex - 1]
      navigate(`/programmes/${programmeId}/programme-workouts/${prevWorkout.id}/workouts/${prevWorkout.workoutId}`)
    }
  }

  const handleNext = (): void => {
    if (hasNext) {
      const nextWorkout = sortedWorkouts[currentIndex + 1]
      navigate(`/programmes/${programmeId}/programme-workouts/${nextWorkout.id}/workouts/${nextWorkout.workoutId}`)
    }
  }

  // Calculate day within the week
  const getDayInWeek = (): number => {
    if (!currentWorkout) return 1

    // Count how many workouts are in the same week before this one
    const workoutsInSameWeek = sortedWorkouts.filter((w) => w.onWeek === currentWorkout.onWeek && w.order <= currentWorkout.order)
    return workoutsInSameWeek.length
  }

  if (isLoading || !programme || !currentWorkout) {
    return <div>Loading...</div>
  }

  const weekNumber = currentWorkout.onWeek
  const dayNumber = getDayInWeek()

  return (
    <div className={styles.container}>
      <Row className="align-items-center mb-3">
        <Col xs={12} md={6}>
          <BackArrow />
        </Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col xs={12} md={8}>
          <div className={styles.programmeInfo}>
            <h2 className={styles.programmeTitle}>{programme.title}</h2>
            <p className={styles.weekInfo}>
              Week {weekNumber}, Day {dayNumber}
            </p>
          </div>
        </Col>
        <Col xs={12} md={4} className="text-md-end">
          <div className={styles.navigation}>
            <Button variant="outline-secondary" onClick={handlePrevious} disabled={!hasPrevious} className="me-2">
              ← Previous
            </Button>
            <Button variant="outline-secondary" onClick={handleNext} disabled={!hasNext}>
              Next →
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  )
}
